import React from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { RadioButton } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";
import "./Form.scss";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import TagInput from "./tagInput";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";

function Form({
  formTitle,
  initialFormData,
  fieldConfig,
  onSubmit,
  onClose,
  isContentFirstSubmit,
  mode
}) {
  const [formData, setFormData] = React.useState(() => ({ ...initialFormData }));
  const [answerFields, setAnswerFields] = React.useState([""]);
  const [correctAnswers, setCorrectAnswers] = React.useState([]);

  React.useEffect(() => {
    if (initialFormData) {
      const updatedFormData = { ...initialFormData };
      if (formTitle === "Question") {
        const initialAnswersData = initialFormData.answers?.title || [];
        const initialAnswers = initialAnswersData.map(item => item.answer || "");
        const initialCorrectAnswers = initialAnswersData.map(item => item.is_correct || false);
        updatedFormData.answers = initialAnswers;
        updatedFormData.correctAnswers = initialCorrectAnswers;
        setAnswerFields(initialAnswers);
        setCorrectAnswers(initialCorrectAnswers);
      }
      setFormData(updatedFormData);
    }
  }, [initialFormData, formTitle]);
  const [validationErrors, setValidationErrors] = React.useState({});
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [isFirstSubmit, setIsFirstSubmit] = React.useState(isContentFirstSubmit);
  const [answerTranslations, setAnswerTranslations] = React.useState([]);
  const toastRef = React.useRef(null);
  const showWarning = (key) => {
    if (key === "publishDate") {
      alert(
        "Before choosing a Publish Date, make sure the compilation & review is done"
      );
    }
  };
  const handleInputChange = (key, value, event) => {
    const field = fieldConfig.find((f) => f.key === key);
    let newDate = value instanceof Date ? value : new Date(value);
    let publishDate = formData.publishDate
      ? new Date(formData.publishDate)
      : null;
    let startDate = formData.startDate ? new Date(formData.startDate) : null;
    const endDate = formData.endDate ? new Date(formData.endDate) : null;
    const lengthLimits = {
      title: 100,
      name: 100,
      description: 500,
      text: 500,
    };
    const onlySpecialChars = /^[^a-zA-Z0-9]*$/;
    if (lengthLimits[key] && value.length > lengthLimits[key]) {
      setValidationErrors((prev) => ({
        ...prev,
        [key]: `${key.charAt(0).toUpperCase() + key.slice(1)} cannot exceed ${lengthLimits[key]} characters.`,
      }));
      return;
    }
    if (key === "title" && onlySpecialChars.test(value)) {
      setValidationErrors((prev) => ({
        ...prev,
        title: "Title cannot be only special characters.",
      }));
      return;
    }
    if (lengthLimits[key] && value.length <= lengthLimits[key]) {
      setValidationErrors((prev) => ({
        ...prev,
        [key]: "",
      }));
    }
    if (key === "endDate" && !startDate) {
      setValidationErrors((prev) => ({
        ...prev,
        endDate: "Please select Start Date first."
      }));
      return;
    }
    if (key === "publishDate" && startDate && newDate < startDate) {
      setValidationErrors((prev) => ({
        ...prev,
        publishDate: "Publish Date must be greater than or equal to Start Date",
      }));
      return;
    } else if (key === "publishDate") {
      setValidationErrors((prev) => ({
        ...prev,
        publishDate: "",
      }));
    }
    if (key === "startDate" && publishDate && newDate > publishDate) {
      setValidationErrors((prev) => ({
        ...prev,
        startDate: "Start Date must be less than or equal to Publish Date",
      }));
      return;
    } else if (key === "startDate") {
      setValidationErrors((prev) => ({
        ...prev,
        startDate: "",
      }));
    }
    if (key === "endDate") {
      if (startDate && newDate < startDate) {
        setValidationErrors((prev) => ({
          ...prev,
          endDate: "End Date must be greater than or equal to Start Date",
        }));
        return;
      }
      if (key === "publishDate" && endDate && newDate > endDate) {
        setValidationErrors((prev) => ({
          ...prev,
          publishDate: "Publish Date cannot be greater than End Date."
        }));
        return;
      }
      if (publishDate && newDate <= publishDate) {
        setValidationErrors((prev) => ({
          ...prev,
          endDate: "End Date must be greater than Publish Date",
        }));
        return;
      }
      if (publishDate && newDate < publishDate) {
        setValidationErrors((prev) => ({
          ...prev,
          endDate: "End Date must be greater than or equal to Publish Date",
        }));
        return;
      }
      setValidationErrors((prev) => ({
        ...prev,
        endDate: "",
      }));
    }
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
    setValidationErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
    if (field?.onChange) {
      field.onChange(event);
    }
  };
  const translateToMarathi = async (text) => {
    const response = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(
        text
      )}&langpair=en|mr`
    );
    const data = await response.json();
    return data.responseData.translatedText;
  };

  const handleTranslationChange = async (key, value) => {
    handleInputChange(key, value);
    const translatedText = await translateToMarathi(value);
    handleInputChange(`${key}_marathi`, translatedText);
  };
  const handleClear = () => {
    setFormData((prev) => {
      const clearedData = {};
      Object.keys(prev).forEach((key) => {
        clearedData[key] = "";
      });
      return clearedData;
    });
    setAnswerFields([""]);
    setCorrectAnswers([]);
    setValidationErrors({});
    setAnswerTranslations([]);
  };
  const handleTotalOptionsChange = (value) => {
    handleInputChange("totalOptions", value);

    const totalOptions = Number(value);

    if (value === "" || isNaN(totalOptions)) {
      setValidationErrors((prev) => ({
        ...prev,
        totalOptions: "Please enter a number between 1 and 5.",
      }));
      return;
    }

    if (totalOptions < 1 || totalOptions > 5) {
      setValidationErrors((prev) => ({
        ...prev,
        totalOptions: "Please enter a number between 1 and 5.",
      }));
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        totalOptions: "",
      }));
      setAnswerFields(Array(totalOptions).fill(""));
      setCorrectAnswers(Array(totalOptions).fill(false));
    }
  };

  const handleAnswerChange = (index, value) => {
    const updatedAnswers = [...answerFields];
    updatedAnswers[index] = value;
    setAnswerFields(updatedAnswers);
    handleInputChange("answers", updatedAnswers);
  };

  const handleCheckboxChange = (selectedIndex) => {
    const updatedCorrectAnswers = answerFields.map((_, i) => i === selectedIndex);
    setCorrectAnswers(updatedCorrectAnswers);
    handleInputChange("correctAnswers", updatedCorrectAnswers);
  };

  const handleQUeAnswerChange = (index, value) => {
    const updatedAnswers = [...answerFields];
    updatedAnswers[index] = value;
    setAnswerFields(updatedAnswers);
    handleInputChange("answers", updatedAnswers);
    translateToMarathi(value).then((translatedText) => {
      const updatedTranslations = [...answerTranslations];
      updatedTranslations[index] = translatedText;
      setAnswerTranslations(updatedTranslations);
      handleInputChange("answers_marathi", updatedTranslations);
    });
  };

  const validateForm = () => {
    const errors = {};

    fieldConfig.forEach((field) => {
      const shouldSkipValidation =
        (field.key === "topicId" || field.key === "moduleId") &&
        formData["quizType"] === "regular";

      if (
        field.required &&
        !shouldSkipValidation &&
        (formData[field.key] === null ||
          formData[field.key] === undefined ||
          formData[field.key] === "")
      ) {
        errors[field.key] = `${field.label} is required`;
      }
      if (field.key === "answers") {
        const answersArray = formData.answers
        const correctAnswersArray = formData.correctAnswers
        if(answersArray.length  === 0 && correctAnswersArray.length === 0 ){
          errors[field.key] = `Options is required`
        }else if(correctAnswersArray.length === 0 ){
          errors[field.key] = `Correct Options is required`
         }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const videoRef = React.useRef(null);
  const thumbnailRef = React.useRef(null);
  const handleSubmit = () => {
    if (validateForm()) {
      const formDataToSubmit = new FormData();
      if (formTitle === "Question") {
        const answersArray = formData.answers
        const answersMarathiArray = formData.answers_marathi
        const correctAnswersArray = formData.correctAnswers
        var answersJson = answersArray.map((answer, index) => ({
          answer,
          is_correct: correctAnswersArray[index] || false,
        }));
        if (mode !== "update") {
          var marathiAnswerJson = answersMarathiArray.map((answer, index) => ({
            answer,
            is_correct: correctAnswersArray[index] || false,
          }));
        }
      }
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "roleId" && Array.isArray(value)) {
          formDataToSubmit.append("roleIds", JSON.stringify(value));
        } else if (value instanceof File) {
          formDataToSubmit.append(key, value);
        } else {
          formDataToSubmit.append(key, value ?? "");
        }
      });
      if (formTitle === "Question") {
        formDataToSubmit.append("answersJson", JSON.stringify(answersJson));
        formDataToSubmit.append("marathiAnswerJson", JSON.stringify(marathiAnswerJson));
      }
      onSubmit(formDataToSubmit);
      if (formTitle === "Question") {
        setShowConfirm(true);
      }
      if (formTitle === "Content" && !isFirstSubmit) {
        setIsFirstSubmit(true);
        setFormData((prev) => ({
          ...prev,
          laungaugeId: "",
          contentType: "",
          path: "",
          video: "",
          thumbnailPath: "",
          tags: [],
        }));
        if (videoRef.current) videoRef.current.value = "";
        if (thumbnailRef.current) thumbnailRef.current.value = "";
      } else {
        setIsFirstSubmit(false);
        setValidationErrors({});
      }
    }
  };

  const handleAddAnother = () => {
    setFormData((prevData) => ({
      ...prevData,
      text: "",
      totalOptions: "",
      isMultiselect: "",
      answer_0: "",
      answer_1: "",
      answer_2: "",
      answer_3: "",
      answer_4: "",
      answer_0_marathi: "",
      answer_1_marathi: "",
      answer_2_marathi: "",
      answer_3_marathi: "",
      answer_4_marathi: "",
      answers: "[]",
      correctAnswers: "[]",
      text_marathi: "",
    }));
    setAnswerFields([""]);
    setCorrectAnswers([]);
    setValidationErrors({});
    setShowConfirm(false);
  };

  return (
    <div className="add-course-form">
      <h2 className="form-title">{mode === "update" ? "Update " : "Add New "} {formTitle}</h2>
      <Divider />
      <ConfirmDialog
        visible={showConfirm}
        onHide={() => setShowConfirm(false)}
        message="Do you want to add another question?"
        header="Question Added"
        icon="pi pi-question-circle"
        accept={handleAddAnother}
        reject={onClose}
      />
      <div className="form-container">
        <div className="form-row">
          {fieldConfig.map((field) => (
            <div key={field.key} className="form-group col-3">
              {(field.key === "path" && formData["contentType"] === "Custom") ||
                (field.key === "video" && formData["contentType"] === "Youtube") ||
                ((field.key === "topicId" || field.key === "moduleId") && formData["quizType"] === "regular") ? (
                <></>
              ) : (
                field.key !== "answers" && (
                  <label htmlFor={field.key} className="form-label">
                    {field.required && (
                      <span className="required-asterisk">*</span>
                    )}{" "}
                    {field.label}
                  </label>
                )
              )}
              {field.type === "text" && field.key === "totalOptions" ? (
                <InputText
                  id={field.key}
                  value={formData[field.key] || ""}
                  onChange={(e) => handleTotalOptionsChange(e.target.value)}
                  placeholder={field.placeholder}
                  className="form-input"
                />
              ) : field.key === "path" &&
                formData["contentType"] === "Custom" ? (
                <></>
              ) : field.type === "text" && field.key === "answers" ? (
                <>
                  <div key={`answers`} className="answer-group">
                    <label htmlFor={`answers`} className="form-label">
                      <span className="required-asterisk">*</span> Options
                    </label>
                    <label htmlFor={`correctAnswers`} className="form-label">
                      <span className="required-asterisk">*</span> Correct Answer
                    </label>
                  </div>
                  {answerFields.map((_, index) => (
                    <div key={`answer_${index}`} className="answer-group">
                      <InputText
                        id={`answer_${index}`}
                        value={answerFields[index]}
                        onChange={(e) => handleAnswerChange(index, e.target.value)}
                        placeholder={`Answer ${index + 1}`}
                        className="form-input"
                      />
                      <RadioButton
                        inputId={`correctAnswer_${index}`}
                        name="correctAnswer"
                        value={index}
                        onChange={(e) => handleCheckboxChange(e.value)}
                        checked={correctAnswers[index]}
                      />
                    </div>
                  ))}
                </>
              ) : (
                field.type === "text" && (
                  <InputText
                    id={field.key}
                    value={formData[field.key]}
                    onChange={(e) =>
                      handleInputChange(field.key, e.target.value)
                    }
                    placeholder={field.placeholder}
                    className="form-input"
                  />
                )
              )}{field.type === "inputtranslation" && field.key === "answers" ? (
                <>
                  <div key={`answers`} className="answer-group">
                    <label htmlFor={`answers`} className="form-label">
                      <span className="required-asterisk">*</span> Options
                    </label>
                    <label htmlFor={`correctAnswers`} className="form-label">
                      <span className="required-asterisk">*</span> Correct Answer
                    </label>
                  </div>
                  {answerFields.map((_, index) => (
                    <div key={`answer_${index}`} className="answer-group">
                      <InputText
                        id={`answer_${index}`}
                        value={answerFields[index]}
                        onChange={(e) => handleQUeAnswerChange(index, e.target.value)}
                        placeholder={`Answer ${index + 1}`}
                        className="form-input"
                      />
                      <InputText
                        id={`answer_${index}_marathi`}
                        value={answerTranslations[index] || ""}
                        onChange={(e) => {
                          const updatedTranslations = [...answerTranslations];
                          updatedTranslations[index] = e.target.value;
                          setAnswerTranslations(updatedTranslations);
                          handleInputChange("answers_marathi", updatedTranslations);
                        }}
                        placeholder="Translated Text (Marathi)"
                        className="form-input translation-box"
                      />
                      <RadioButton
                        inputId={`correctAnswer_${index}`}
                        name="correctAnswer"
                        value={index}
                        onChange={(e) => handleCheckboxChange(e.value)}
                        checked={correctAnswers[index]}
                      />
                    </div>
                  ))}
                </>
              ) : (
                field.type === "inputtranslation" && (
                  <>
                    <InputText
                      id={field.key}
                      value={formData[field.key] || ""}
                      onChange={(e) => handleTranslationChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="form-input"
                    />
                    <InputText
                      id={`${field.key}_marathi`}
                      value={formData[`${field.key}_marathi`] || ""}
                      onChange={(e) =>
                        handleInputChange(`${field.key}_marathi`, e.target.value)
                      }
                      placeholder="Translated Text (Marathi)"
                      className="form-input translation-box"
                    />
                  </>
                )
              )}
              {field.type === "chips" && (
                <TagInput
                  value={formData[field.key] || []}
                  onChange={(newTags) => handleInputChange(field.key, newTags)}
                />
              )}
              {(field.key === "topicId" || field.key === "moduleId") &&
                formData["quizType"] === "regular" ? (
                <></>
              ) : (field.type === "dropdown" && (
                <Dropdown
                  id={field.key}
                  value={formData[field.key]}
                  options={field.options}
                  onChange={(e) => handleInputChange(field.key, e.value, e)}
                  placeholder={field.placeholder}
                  className="form-dropdown"
                  filter
                  filterBy="label"
                />
              ))}
              {field.key === "video" &&
                formData["contentType"] === "Youtube" ? (
                <></>
              ) : (
                field.type === "file" && (
                  <InputText
                    id={field.key}
                    type="file"
                    accept={field.accept}
                    ref={
                      field.key === "video"
                        ? videoRef
                        : field.key === "thumbnailPath"
                          ? thumbnailRef
                          : null
                    }
                    onChange={(e) =>
                      handleInputChange(field.key, e.target.files[0])
                    }
                    className="form-file"
                  />
                )
              )}
              {field.type === "radio" && (
                <div className="flex gap-3">
                  {field.options.map((option) => (
                    <div key={option.value} className="flex align-items-center">
                      <RadioButton
                        inputId={`${field.key}_${option.value}`}
                        name={field.key}
                        value={option.value}
                        onChange={(e) => handleInputChange(field.key, e.value)}
                        checked={formData[field.key] === option.value}
                      />
                      <label
                        htmlFor={`${field.key}_${option.value}`}
                        className="ml-2"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {field.type === "multiselect" && (
                <MultiSelect
                  id={field.key}
                  value={formData[field.key] || []}
                  options={field.options}
                  onChange={(e) => handleInputChange(field.key, e.value)}
                  placeholder={field.placeholder}
                  className="form-multiselect"
                  display="chip"
                />
              )}
              {field.type === "calendar" && (
                <Calendar
                  id={field.key}
                  value={formData[field.key] || null}
                  onChange={(e) => handleInputChange(field.key, e.value)}
                  onFocus={() => showWarning(field.key)}
                  placeholder={field.placeholder}
                  dateFormat="dd/mm/yy"
                  showIcon
                  className="form-calendar"
                />
              )}

              {validationErrors[field.key] && (
                <small className="p-error">{validationErrors[field.key]}</small>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="form-actions">
        <Button
          label={!isFirstSubmit ? "Submit" : "Add more"}
          icon={!isFirstSubmit ? "pi pi-check" : "pi pi-plus"}
          className="p-button-success"
          onClick={handleSubmit}
        />
        <Button
          label="Clear"
          icon="pi pi-times"
          className="p-button-secondary ml-2"
          onClick={handleClear}
        />
      </div>
      <Toast ref={toastRef} />
    </div>
  );
}

export default Form;